import appToast from '../../utilities/appToast';
import { Alert, Button, Form, Input, Row, Typography } from 'antd';
import { Link, useSearchParams } from 'react-router-dom'
import { useState } from 'react'
import config from '../../config'
import useTranslation from '../../localization/useTranslation'
import LanguageToggler from '../../components/LanguageToggler/LanguageToggler'
import { getLanguageHeaders } from '../../store/services/http-common'
import { clearAuthSession } from '../../utilities/authentication'

const redirectToLogin = (email) => {
    clearAuthSession()
    const params = email ? `?email=${encodeURIComponent(email)}` : ''
    window.location.assign(`/login${params}`)
}

const ResetPassword = () => {
    const [form] = Form.useForm()
    const { t, language } = useTranslation()
    const [searchParams] = useSearchParams()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const token = searchParams.get('token')
    const email = searchParams.get('email')

    const onFinish = async (values) => {
        if (!token || !email) {
            setError(t('auth.resetPassword.invalidLink'))
            return
        }

        setLoading(true)
        setError(null)

        try {
            const response = await fetch(`${config.apiUrl}/reset-password`, {
                method: 'POST',
                body: JSON.stringify({
                    token,
                    email,
                    password: values.password,
                    password_confirmation: values.password_confirmation,
                }),
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    ...getLanguageHeaders(),
                },
            })

            const data = await response.json()

            if (!response.ok) {
                const validationMessage = data?.errors
                    ? Object.values(data.errors).flat()[0]
                    : data?.message
                setError(validationMessage ?? t('auth.resetPassword.failed'))
                return
            }

            appToast.success(data?.message ? t(data.message) : t('auth.resetPassword.success'))
            setTimeout(() => redirectToLogin(email), 800)
        } catch (err) {
            setError(err?.message ?? t('auth.resetPassword.failed'))
        } finally {
            setLoading(false)
        }
    }

    if (!token || !email) {
        return (
            <Row style={{ height: '100vh', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 1000 }}>
                    <LanguageToggler variant="round" />
                </div>
                <div style={{ width: 420, maxWidth: '92%', padding: 20, boxSizing: 'border-box' }}>
                    <Alert
                        message={t('auth.resetPassword.error')}
                        description={t('auth.resetPassword.invalidLink')}
                        type="error"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                    <Button block type="primary" onClick={() => redirectToLogin()}>
                        {t('auth.forgotPassword.backToLogin')}
                    </Button>
                </div>
            </Row>
        )
    }

    return (
        <Row style={{ height: '100vh', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 1000 }}>
                <LanguageToggler variant="round" />
            </div>

            <div style={{ width: 420, maxWidth: '92%', padding: 20, boxSizing: 'border-box' }}>
                <div style={{ textAlign: 'center', marginBottom: 8 }}>
                    <Typography.Title style={{ marginBottom: 8 }}>{t('auth.resetPassword.title')}</Typography.Title>
                    <Typography.Paragraph type="secondary">{t('auth.resetPassword.description')}</Typography.Paragraph>
                </div>

                {error ? <Alert message={error} type="error" showIcon closable style={{ marginBottom: 16 }} onClose={() => setError(null)} /> : null}

                <Form dir={language === 'ar' ? 'rtl' : 'ltr'} form={form} onFinish={onFinish} labelCol={{ span: 24 }}>
                    <Form.Item
                        name="password"
                        label={t('auth.resetPassword.password.label')}
                        rules={[
                            { required: true, message: t('auth.resetPassword.password.required') },
                            { min: 8, message: t('auth.resetPassword.password.minLength') },
                        ]}
                    >
                        <Input.Password placeholder={t('auth.resetPassword.password.placeholder')} autoComplete="new-password" />
                    </Form.Item>
                    <Form.Item
                        name="password_confirmation"
                        label={t('auth.resetPassword.password.confirm')}
                        dependencies={['password']}
                        rules={[
                            { required: true, message: t('auth.resetPassword.password.confirmRequired') },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve()
                                    }
                                    return Promise.reject(new Error(t('auth.resetPassword.password.mismatch')))
                                },
                            }),
                        ]}
                    >
                        <Input.Password placeholder={t('auth.resetPassword.password.confirmPlaceholder')} autoComplete="new-password" />
                    </Form.Item>
                    <Form.Item>
                        <Button block type="primary" htmlType="submit" loading={loading}>
                            {t('auth.resetPassword.submit')}
                        </Button>
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0, textAlign: 'center' }}>
                        <Link to="/login">{t('auth.forgotPassword.backToLogin')}</Link>
                    </Form.Item>
                </Form>
            </div>
        </Row>
    )
}

export default ResetPassword
